import { FC, memo, useEffect, useMemo, useState } from "react";
import { IModelsTree, ITreeObjectModel } from "Data/interfaces/FederatedViewer/IModelsTree";
import { Tree } from 'antd';
import Ellipsis from "Components/UI/Ellipsis";
import { Icon } from "Components/UI";
import styles from './VirtualTreeView.module.scss';
import './VirtualTreeView.css';

// Fora do componente de propósito: `memo()` chamado no corpo cria um TIPO de
// componente novo a cada render, e o React desmonta e remonta o subtree em vez
// de reaproveitá-lo — o oposto do que o memo existe para fazer.
const EllipsisMemo = memo(Ellipsis);
const IconMemo = memo(Icon);

interface IVirtualTreeView {
  result: IModelsTree;
  hideNodes: number[];
  showNodes: number[];
  selectedNode?: number | null;
  onToggleNode: (node: number, urn: string) => void;
  onToggleSelectNode: (node: number, urn: string) => void;
}

/**
 * Ids ocultos, já com a herança resolvida: um nó está oculto se ele mesmo foi
 * escondido OU se algum ancestral escondeu — a menos que ele tenha sido
 * explicitamente mostrado depois.
 *
 * Isto era calculado dentro do worker, junto com a árvore. Separar é o que faz
 * o olho responder sem reconstruir nada: esconder um nó mexe só neste Set, não
 * no `treeData`.
 */
const collectHidden = (
  root: ITreeObjectModel | undefined,
  hideNodes: number[],
  showNodes: number[],
): Set<number> => {
  const hidden = new Set<number>();
  if (!root) { return hidden; }

  const hideSet = new Set(hideNodes);
  const showSet = new Set(showNodes);

  const walk = (node: ITreeObjectModel, parentHidden: boolean) => {
    const isHidden = (parentHidden || hideSet.has(node.id)) && !showSet.has(node.id);
    if (isHidden) { hidden.add(node.id); }
    node.children?.forEach((child) => walk(child, isHidden));
  };

  walk(root, false);
  return hidden;
};

const VirtualTreeView: FC<IVirtualTreeView> = ({
  result,
  hideNodes,
  showNodes,
  selectedNode,
  onToggleNode,
  onToggleSelectNode,
}) => {
  const [treeData, setTreeData] = useState<any[]>([]);

  const hiddenIds = useMemo(
    () => collectHidden(result.list[0], hideNodes, showNodes),
    [result, hideNodes, showNodes],
  );

  // Só a ESTRUTURA depende do worker. Visibilidade e seleção saíram das deps:
  // as duas são lidas na hora de desenhar a linha, então tê-las aqui recriava o
  // worker inteiro — com serialização da árvore na ida e na volta — por um
  // clique que não muda nó nenhum de lugar.
  useEffect(() => {
    const worker = new Worker(new URL('./treeWorker.js', import.meta.url));
    worker.onmessage = (e) => {
      setTreeData(e.data);
    };

    worker.postMessage({
      level: 0,
      array: result.list[0]?.children || [],
      rootId: result.list[0]?.id,
    });

    return () => {
      worker.terminate();
    };
  }, [result]);

  return (
    <Tree
      height={400}
      treeData={treeData}
      titleRender={(item: any) => {
        const isHidden = hiddenIds.has(item.metadata.id);

        return (
          <span
            className={`${styles['treeNodeRow']} ${item.metadata.id === selectedNode ? styles['treeNodeRowActive'] : ''}`}
            onClick={() => onToggleSelectNode(item.metadata.id, result.urn)}
          >
            <EllipsisMemo
              phrase={item.title}
              maxLength={17}
            />
            <span
              onClick={(e) => {
                e.stopPropagation();
                onToggleNode(item.metadata.id, result.urn);
              }}
            >
              <IconMemo
                icon={isHidden ? 'notVisualizar' : 'visualizar'}
                customSize={11}
                customColor={isHidden ? '#BEC2C6' : '#324150'}
              />
            </span>
          </span>
        );
      }}
    />
  );
};

export default VirtualTreeView;
